// =====================================================
// MODAL: BulkCreateTaskModal
// Description: Modale pour créer une tâche/relance sur plusieurs entités en masse
// Item #16 retours client : créer des relances en masse depuis la sélection
//                           multi-page (équipages/contacts/prospects).
// =====================================================

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronDown, User, Users } from 'lucide-react';
import { createTaskSchema, type CreateTaskInput } from '@/schemas/task';
import { Modal, ModalFooter } from '../ui/modal';
import { Form } from '../ui/form';
import { FormInput, FormSelect, FormTextarea } from '../ui';
import { AssignUserModal } from './AssignUserModal';
import type { EntityType } from '@/types/timeline';
import { useSupabase } from '@/hooks/useSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useUsers } from '@/contexts/UsersContext';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';

interface BulkCreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Type d'entité concerné. Pour les prospects, on stocke `entity_type='prospect'`
   * et `entity_id = form_submission.id` (cohérent avec CreateTaskModal).
   */
  entityType: EntityType;
  /** IDs des entités sélectionnées (form_submission ids pour les prospects) */
  entityIds: string[];
  /** Nom de l'entité au pluriel pour l'UI */
  entityNamePlural: string;
  onSuccess?: () => void;
}

const TASK_TYPE_OPTIONS = [
  { value: 'call', label: 'Appel' },
  { value: 'email', label: 'Mail' },
  { value: 'meeting', label: 'Réunion' },
  { value: 'other', label: 'Autres' },
];

export function BulkCreateTaskModal({
  isOpen,
  onClose,
  entityType,
  entityIds,
  entityNamePlural,
  onSuccess,
}: BulkCreateTaskModalProps) {
  const supabase = useSupabase();
  const { user } = useAuth();
  const { users } = useUsers();
  const { canManageAssignments } = usePermissions();
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);

  const form = useForm<CreateTaskInput>({
    resolver: zodResolver(createTaskSchema),
    defaultValues: {
      title: '',
      type: 'call',
      date: '',
      time: '',
      assigned_to: null,
      notes: '',
    },
  });

  const selectedUser = users.find((u) => u.id === form.watch('assigned_to'));

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleFormSubmit = async (data: CreateTaskInput) => {
    if (entityIds.length === 0) {
      toast.error('Aucune entité sélectionnée');
      return;
    }

    try {
      // Construire la date complète en heure locale
      let dueDate: string;
      if (data.time) {
        const [hours, minutes] = data.time.split(':').map(Number);
        const localDate = new Date(data.date);
        localDate.setHours(hours, minutes, 0, 0);
        dueDate = localDate.toISOString();
      } else {
        const localDate = new Date(data.date);
        localDate.setHours(12, 0, 0, 0);
        dueDate = localDate.toISOString();
      }

      // Si l'utilisateur ne peut pas gérer les assignations (rôle 'user'),
      // auto-assigner toutes les tâches à lui-même
      const assignedTo = canManageAssignments() ? data.assigned_to : user?.id || null;

      // Bulk insert : une tâche par entité sélectionnée
      const tasksToInsert = entityIds.map((entityId) => ({
        entity_type: entityType,
        entity_id: entityId,
        type: data.type,
        title: data.title,
        notes: data.notes || null,
        due_date: dueDate,
        completed: false,
        assigned_to: assignedTo,
        created_by: user?.id || null,
      }));

      const { error: insertError } = await supabase.from('tasks').insert(tasksToInsert);

      if (insertError) throw insertError;

      // Logger un événement timeline par entité
      const taskTypeLabel = TASK_TYPE_OPTIONS.find((t) => t.value === data.type)?.label;
      const timelineEvents = entityIds.map((entityId) => ({
        entity_type: entityType,
        entity_id: entityId,
        event_type: 'task_created',
        title: `Tâche créée (en masse) : ${data.title}`,
        description: `Type: ${taskTypeLabel}`,
        created_by: user?.id || null,
        metadata: {
          task_type: data.type,
          due_date: dueDate,
          title: data.title,
          is_bulk: true,
        },
      }));

      const { error: timelineError } = await supabase
        .from('timeline_events')
        .insert(timelineEvents);

      if (timelineError) {
        // Ne bloque pas si le log échoue
        console.error('Erreur log timeline:', timelineError);
      }

      toast.success('Relances créées', {
        description: `${entityIds.length} tâche(s) créée(s) avec succès`,
      });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      console.error('Erreur lors de la création des tâches en masse:', error);
      toast.error('Erreur', {
        description: 'Impossible de créer les relances',
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer une relance"
      size="lg"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel={`Créer ${entityIds.length} relance${entityIds.length > 1 ? 's' : ''}`}
          isSubmitting={form.formState.isSubmitting}
          formId="bulk-create-task-form"
        />
      }
    >
      {/* Info sur la sélection */}
      <div className="mb-4 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
        <div className="flex items-center gap-2 text-sm text-zinc-600">
          <Users className="size-4" />
          <span>
            <strong>{entityIds.length}</strong> {entityNamePlural} sélectionné
            {entityIds.length > 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <Form {...form}>
        <form id="bulk-create-task-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            {/* Titre */}
            <FormInput
              control={form.control as any}
              name="title"
              label="Titre"
              placeholder="Ex: Relance BED incomplet"
              required
            />

            {/* Type */}
            <FormSelect
              control={form.control as any}
              name="type"
              label="Type"
              required
              options={TASK_TYPE_OPTIONS}
            />

            {/* Date et Heure */}
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                control={form.control as any}
                name="date"
                label="Date de relance"
                type="date"
                required
              />
              <FormInput
                control={form.control as any}
                name="time"
                label="Heure (optionnel)"
                type="time"
                placeholder="09:00"
              />
            </div>

            {/* Assigné à - Visible uniquement pour admin et superadmin */}
            {canManageAssignments() && (
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Assigné à</label>
                <button
                  type="button"
                  onClick={() => setIsUserModalOpen(true)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm border border-zinc-300 rounded-md bg-white hover:bg-zinc-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <User className="size-4 text-zinc-400" />
                    <span className={selectedUser ? 'text-zinc-900' : 'text-zinc-500'}>
                      {selectedUser ? selectedUser.full_name || selectedUser.email : 'Non assigné'}
                    </span>
                  </div>
                  <ChevronDown className="size-4 text-zinc-400" />
                </button>
              </div>
            )}

            {/* Description */}
            <FormTextarea
              control={form.control as any}
              name="notes"
              label="Description (optionnel)"
              placeholder="Description partagée par toutes les relances..."
              rows={3}
            />
          </div>
        </form>
      </Form>

      {/* Modale d'assignation d'utilisateur */}
      <AssignUserModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        currentUserId={form.watch('assigned_to')}
        onAssign={async (userId) => {
          form.setValue('assigned_to', userId);
        }}
        onUnassign={async () => {
          form.setValue('assigned_to', null);
        }}
        showUnassignOption={true}
        title="Assigner les relances à"
      />
    </Modal>
  );
}
